import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  CreditCard,
  Share2,
  RefreshCw,
  Check,
  MessageSquare,
  Calendar,
} from 'lucide-react';
import { useDriverProfile } from '@/hooks/useDriverProfile';
import { companyIdFromProfileOrStorage } from '@/hooks/userCompanies.shared';
import { usePaymentStatus } from '@/hooks/usePaymentStatus';
import { useCreateQuickInvoice } from '@/hooks/useCreateQuickInvoice';
import { useGeneratePaymentLink } from '@/hooks/useGeneratePaymentLink';
import { useRefreshPaymentStatus } from '@/hooks/useRefreshPaymentStatus';
import { useMarkInvoiceAsPaid } from '@/hooks/useMarkInvoiceAsPaid';
import {
  useDriverPermissions,
  useHasPermission,
} from '@/hooks/useDriverPermissions';
import PermissionsErrorAlert from '@/components/common/PermissionsErrorAlert';
import { Alert } from '@relay-towing/ui';
import { useToast } from '@/contexts/ToastContext';
import { useDriverCall } from '@/hooks/useDriverCall';
import { useGetRates } from '@/hooks/useGetRates';
import { api } from '@/lib/api';
import { useHybridAuth } from '@/hooks/useHybridAuth';
import { paymentLinkStorage } from '@/services/paymentApi';
import {
  ServiceType,
  TowType,
  calculateDaysHeld,
  getVehicleTypePrice,
  getBillingStartTime,
  type Dispatch,
  type Impound,
} from '@/lib/calculations';
import type { Rate } from '@/hooks/useGetRates';
import PaymentStatusBadge from './components/PaymentStatusBadge';
import PaymentsNotConnected from './components/PaymentsNotConnected';
import InvoiceItemsEditor from './components/InvoiceItemsEditor';
import QRCodeCard from './components/QRCodeCard';
import type { InvoiceItem } from '@/types/payments';

interface PaymentScreenProps {
  dispatchId: string;
  onBack: () => void;
}

const PaymentScreen: React.FC<PaymentScreenProps> = ({
  dispatchId,
  onBack,
}) => {
  const [errorState, setErrorState] = useState<string | null>(null);
  const { showToast } = useToast();
  const { getAccessTokenSilently } = useHybridAuth();

  // Phase 2: Local state for invoice creation
  const [amount, setAmount] = useState<number>(0);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [customerName, setCustomerName] = useState<string>('');
  const [paymentType, setPaymentType] = useState<'CASH' | 'SQUARE'>('SQUARE');
  const [defaultItemsPopulated, setDefaultItemsPopulated] = useState(false);
  const [customerNameInitialized, setCustomerNameInitialized] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState<Dispatch | null>(
    null
  );
  const [selectedDispatchImpound, setSelectedDispatchImpound] =
    useState<Impound | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [isGeneratingPaymentLink, setIsGeneratingPaymentLink] = useState(false);

  // Phase 3-6: Payment link and actions state
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Step 1: Add driver profile hook
  const {
    data: driverProfile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useDriverProfile();

  // The placeholder profile of an owner with no Driver row has companyId ''.
  const companyId = companyIdFromProfileOrStorage(driverProfile) || undefined;

  // Check permissions
  const canCreateInvoice = useHasPermission('invoices.create');
  const canSendSMS = useHasPermission('invoices.send_sms');
  // The payment link, its status refresh and Mark as Paid are all taking a
  // payment; the backend refuses each of them without this.
  const canTakePayment = useHasPermission('invoices.take_payment');
  const permissionsState = useDriverPermissions();
  const permissionsKnown =
    !permissionsState.isLoading && !permissionsState.isError;

  console.log('[PaymentScreen] Driver profile:', {
    hasProfile: !!driverProfile,
    companyId,
    isLoading: isProfileLoading,
    error: profileError,
  });

  // Step 2: Add payment status hook
  const paymentStatusResult = usePaymentStatus(dispatchId, companyId);

  const paymentStatus = paymentStatusResult.data;
  const isPaymentLoading = paymentStatusResult.isLoading;
  const paymentError = paymentStatusResult.error;

  // Phase 2: Create invoice mutation
  const createInvoiceMutation = useCreateQuickInvoice(companyId);

  // Phase 3-6: Payment actions mutations
  const generateLinkMutation = useGeneratePaymentLink(dispatchId, companyId);
  const refreshStatusMutation = useRefreshPaymentStatus(dispatchId, companyId);
  const markAsPaidMutation = useMarkInvoiceAsPaid(dispatchId, companyId);

  // Fetch dispatch details
  const { data: dispatchData } = useDriverCall(dispatchId, {
    enabled: !!dispatchId,
  });

  // Fetch all company rates
  const { data: allCompanyRates = [] } = useGetRates(
    { companyId },
    { enabled: !!companyId }
  );

  // Filter rates by customer
  const customerRates = customerId
    ? allCompanyRates.filter((rate) => rate.customerId === customerId)
    : [];

  console.log('[PaymentScreen] Payment status:', {
    hasStatus: !!paymentStatus,
    isLoading: isPaymentLoading,
    error: paymentError,
    paymentProvider: paymentStatus?.paymentProvider,
    paymentConnected: paymentStatus?.paymentConnected,
  });

  // Restore payment link from localStorage on mount
  useEffect(() => {
    const storedLink = paymentLinkStorage.getPaymentLink(dispatchId);
    if (storedLink) {
      setPaymentLink(storedLink);
    }
  }, [dispatchId]);

  // Initialize amount and payment link from payment status
  useEffect(() => {
    if (paymentStatus) {
      if (paymentStatus.invoice) {
        const invoiceTotal = paymentStatus.invoice.total;
        if (typeof invoiceTotal === 'number' && invoiceTotal >= 0) {
          setAmount(invoiceTotal);
          setItems(paymentStatus.invoice.items || []);
        }
        // Set payment link if available (prioritize localStorage, then API response)
        const storedLink = paymentLinkStorage.getPaymentLink(dispatchId);
        if (storedLink) {
          setPaymentLink(storedLink);
        } else if (paymentStatus.invoice.paymentLink) {
          setPaymentLink(paymentStatus.invoice.paymentLink);
          // Save to localStorage for future use
          paymentLinkStorage.savePaymentLink(
            dispatchId,
            paymentStatus.invoice.paymentLink
          );
        }
        setDefaultItemsPopulated(true); // Don't populate defaults if invoice exists
        // If invoice exists, use invoice customer name if available, otherwise use contact name
        if (!customerNameInitialized) {
          const invoiceCustomerName = (paymentStatus.invoice as any)
            ?.customerName;
          if (invoiceCustomerName) {
            setCustomerName(invoiceCustomerName);
          } else if (paymentStatus.contactName) {
            setCustomerName(paymentStatus.contactName);
          }
          setCustomerNameInitialized(true);
        }
      } else {
        const suggestedTotal = paymentStatus.suggestedTotal;
        if (typeof suggestedTotal === 'number' && suggestedTotal >= 0) {
          setAmount(suggestedTotal);
        }
        // Auto-populate customer name from contact name on initial load
        if (!customerNameInitialized && paymentStatus.contactName) {
          setCustomerName(paymentStatus.contactName);
          setCustomerNameInitialized(true);
        }
      }
    }
  }, [paymentStatus, customerNameInitialized, dispatchId]);

  // Fetch dispatch and impound data
  useEffect(() => {
    if (dispatchData && companyId) {
      // Map dispatch data to our Dispatch type
      const dispatch: Dispatch = {
        id: dispatchData.id,
        account: dispatchData.account,
        towType:
          (dispatchData.vehicle?.vehicleType?.toUpperCase() as TowType) ||
          TowType.ANY,
        completionTime: dispatchData.completionTime || undefined,
        createdAt: dispatchData.createdAt,
        startedAt: dispatchData.startedAt || undefined,
        arrivedAt: dispatchData.arrivedAt || undefined,
        mileage: undefined, // DriverCall doesn't have mileage field
      };
      setSelectedDispatch(dispatch);
      setCustomerId(dispatchData.account);

      // Fetch impound data for this dispatch
      const fetchImpoundData = async () => {
        try {
          const token = await getAccessTokenSilently();
          const headers: Record<string, string> = {
            Authorization: `Bearer ${token}`,
            'x-company-id': companyId,
          };
          const impoundResponse = await api.get(
            `/impounds/by-dispatch/${dispatchId}`,
            { headers }
          );
          if (impoundResponse.data) {
            const impound: Impound = {
              impoundDate: impoundResponse.data.impoundDate,
              releasedDate: impoundResponse.data.releasedDate || null,
            };
            setSelectedDispatchImpound(impound);
          }
        } catch (error) {
          // No impound data found, that's okay
          setSelectedDispatchImpound(null);
        }
      };
      fetchImpoundData();
    }
  }, [dispatchData, companyId, dispatchId, getAccessTokenSilently]);

  // Populate line items based on rates and dispatch data (following CreateInvoicePageV2 pattern)
  const populateLineItemsFromTemplate = () => {
    const newLineItems: InvoiceItem[] = [];

    if (!customerRates || customerRates.length === 0) {
      console.warn(
        '[PaymentScreen] No customer rates available for line item population'
      );
      return;
    }

    if (!selectedDispatch) {
      console.warn('[PaymentScreen] No dispatch data available');
      return;
    }

    // Calculate storage days from impound data
    const storageDays = selectedDispatchImpound
      ? calculateDaysHeld(
          selectedDispatchImpound.impoundDate,
          selectedDispatchImpound.releasedDate
        )
      : 1; // Default to 1 day if no impound data

    const vehicleType = selectedDispatch.towType || TowType.ANY;

    // Handle TOW services
    // Find TOW_BASE rate
    const towBaseRate = customerRates.find(
      (r) => r.serviceType === ServiceType.TOW_BASE
    ) as Rate | undefined;
    if (towBaseRate) {
      const price = getVehicleTypePrice(towBaseRate, vehicleType);
      if (price > 0) {
        newLineItems.push({
          description: 'Tow Base',
          quantity: 1,
          unitPrice: price,
        });
      }
    }

    // Calculate tow duration if dispatch is available
    let towDurationInHours = 0;
    if (selectedDispatch && selectedDispatch.completionTime) {
      const startTime = getBillingStartTime(selectedDispatch);
      const endTime = new Date(selectedDispatch.completionTime);
      towDurationInHours =
        (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
    }

    // Determine which time-based rate to use
    const towHourMinimumRate = customerRates.find(
      (r) => r.serviceType === ServiceType.TOW_HOUR_MINIMUM
    ) as Rate | undefined;
    const towQuarterHourRate = customerRates.find(
      (r) => r.serviceType === ServiceType.TOW_QUARTER_HOUR
    ) as Rate | undefined;
    const towHalfHourRate = customerRates.find(
      (r) => r.serviceType === ServiceType.TOW_HALF_HOUR
    ) as Rate | undefined;
    const towHourlyRate = customerRates.find(
      (r) => r.serviceType === ServiceType.TOW_HOURLY
    ) as Rate | undefined;
    const towPerMileRate = customerRates.find(
      (r) => r.serviceType === ServiceType.TOW_PER_MILE
    ) as Rate | undefined;

    // Add time-based tow charge
    if (towDurationInHours <= 1 && towHourMinimumRate) {
      // Duration <= 1 hour with TOW_HOUR_MINIMUM
      const price = getVehicleTypePrice(towHourMinimumRate, vehicleType);
      if (price > 0) {
        newLineItems.push({
          description: 'Tow Hour Minimum',
          quantity: 1,
          unitPrice: price,
        });
      }
    } else if (towDurationInHours > 1) {
      // Duration > 1 hour - use appropriate time interval rate
      const remainingMinutes = towDurationInHours * 60;

      if (towHourlyRate) {
        const price = getVehicleTypePrice(towHourlyRate, vehicleType);
        if (price > 0) {
          const fullHours = Math.floor(remainingMinutes / 60);
          let remainingAfterHours = remainingMinutes - fullHours * 60;

          if (towHalfHourRate) {
            const halfHours = Math.floor(remainingAfterHours / 30);
            remainingAfterHours -= halfHours * 30;

            if (towQuarterHourRate) {
              const quarterHours = Math.ceil(remainingAfterHours / 15);
              const totalQty =
                fullHours +
                (halfHours > 0 ? 1 : 0) +
                (quarterHours > 0 ? 1 : 0);
              newLineItems.push({
                description: 'Tow Hourly',
                quantity: totalQty,
                unitPrice: price,
              });
            } else {
              const totalQty = fullHours + (halfHours > 0 ? 1 : 0);
              newLineItems.push({
                description: 'Tow Hourly',
                quantity: totalQty,
                unitPrice: price,
              });
            }
          } else {
            newLineItems.push({
              description: 'Tow Hourly',
              quantity: Math.ceil(remainingMinutes / 60),
              unitPrice: price,
            });
          }
        }
      } else if (towHalfHourRate) {
        const price = getVehicleTypePrice(towHalfHourRate, vehicleType);
        if (price > 0) {
          const halfHours = Math.ceil(remainingMinutes / 30);
          newLineItems.push({
            description: 'Tow Half Hour',
            quantity: halfHours,
            unitPrice: price,
          });
        }
      } else if (towQuarterHourRate) {
        const price = getVehicleTypePrice(towQuarterHourRate, vehicleType);
        if (price > 0) {
          const quarterHours = Math.ceil(remainingMinutes / 15);
          newLineItems.push({
            description: 'Tow Quarter Hour',
            quantity: quarterHours,
            unitPrice: price,
          });
        }
      } else if (towPerMileRate) {
        const price = getVehicleTypePrice(towPerMileRate, vehicleType);
        if (price > 0) {
          newLineItems.push({
            description: 'Tow/Mile',
            quantity: 1,
            unitPrice: price,
          });
        }
      }
    } else if (!towHourMinimumRate && towQuarterHourRate) {
      // No TOW_HOUR_MINIMUM available - use quarter hour rate
      const price = getVehicleTypePrice(towQuarterHourRate, vehicleType);
      if (price > 0) {
        newLineItems.push({
          description: 'Tow Quarter Hour',
          quantity: 1,
          unitPrice: price,
        });
      }
    }

    // Handle STORAGE services (Storage Daily and Storage Base)
    const storageBaseRate = customerRates.find(
      (r) => r.serviceType === ServiceType.STORAGE_BASE
    ) as Rate | undefined;
    if (storageBaseRate) {
      const price = getVehicleTypePrice(storageBaseRate, vehicleType);
      if (price > 0) {
        newLineItems.push({
          description: 'Storage Base',
          quantity: 1,
          unitPrice: price,
        });
      }
    }

    const storageDailyRate = customerRates.find(
      (r) => r.serviceType === ServiceType.STORAGE_DAILY
    ) as Rate | undefined;
    if (storageDailyRate) {
      const price = getVehicleTypePrice(storageDailyRate, vehicleType);
      if (price > 0) {
        newLineItems.push({
          description: 'Storage Daily',
          quantity: storageDays,
          unitPrice: price,
        });
      }
    }

    // Handle LABOR services
    const laborRate = customerRates.find(
      (r) => r.serviceType === ServiceType.LABOR
    ) as Rate | undefined;
    if (laborRate) {
      const price = getVehicleTypePrice(laborRate, vehicleType);
      if (price > 0) {
        // Use quarter hour rate (divided by 4 since it's per hour)
        const quarterHourRate = price / 4;
        newLineItems.push({
          description: 'Labor Quarter Hour',
          quantity: 1,
          unitPrice: quarterHourRate,
        });
      }
    }

    console.log('[PaymentScreen] Populated line items:', {
      itemCount: newLineItems.length,
      items: newLineItems,
      storageDays,
    });

    // Only set items if we found matching rates
    if (newLineItems.length > 0) {
      setItems(newLineItems);
      const total = newLineItems.reduce(
        (sum, item) => sum + item.quantity * item.unitPrice,
        0
      );
      setAmount(total);
      setDefaultItemsPopulated(true);
    }
  };

  // Auto-populate line items when creating new invoice (following CreateInvoicePageV2 pattern)
  useEffect(() => {
    if (
      paymentStatus &&
      !paymentStatus.invoice &&
      !defaultItemsPopulated &&
      items.length === 0 &&
      customerRates.length > 0 &&
      selectedDispatch &&
      customerId
    ) {
      console.log('[PaymentScreen] Populating line items from rates');
      populateLineItemsFromTemplate();
    }
  }, [
    paymentStatus,
    defaultItemsPopulated,
    items.length,
    customerRates.length,
    selectedDispatch,
    customerId,
    selectedDispatchImpound,
  ]);

  // Error handling effect
  useEffect(() => {
    if (profileError) {
      console.error('[PaymentScreen] Profile error:', profileError);
      setErrorState('Failed to load driver profile');
    } else if (paymentError) {
      console.error('[PaymentScreen] Payment status error:', paymentError);
      setErrorState('Failed to load payment information');
    } else {
      // Clear error state when data loads successfully
      if (paymentStatus && driverProfile) {
        setErrorState(null);
      }
    }
  }, [profileError, paymentError, paymentStatus, driverProfile]);

  // Phase 2: Handle creating invoice
  const handleCreateInvoice = async () => {
    try {
      if (!companyId) {
        throw new Error('Company ID is not available');
      }

      // Calculate total from items if items exist, otherwise use amount
      const calculatedTotal =
        items.length > 0
          ? items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0)
          : amount;

      if (calculatedTotal <= 0 && items.length === 0) {
        showToast('error', 'Please enter a valid amount or add line items');
        return;
      }

      // Check if invoice already exists before attempting to create
      if (hasInvoice) {
        showToast('error', 'An invoice already exists for this dispatch');
        await paymentStatusResult.refetch(); // Refresh to get latest status
        return;
      }

      const isCashPayment = paymentType === 'CASH';

      console.log('[PaymentScreen] Creating invoice with:', {
        paymentType,
        isCashPayment,
        dispatchId,
      });

      const invoiceResult = await createInvoiceMutation.mutateAsync({
        dispatchId,
        totalOverride: items.length === 0 ? amount : undefined,
        items: items.length > 0 ? items : undefined,
        customerId: customerId || undefined,
        clientName: customerName || undefined,
        isCashPayment,
      });

      console.log('[PaymentScreen] Invoice created, result:', invoiceResult);

      showToast('success', 'Invoice created successfully');

      // Refresh payment status to get the invoice
      await paymentStatusResult.refetch();

      // Only generate payment link for non-cash payments, and only for a role
      // that may take one: otherwise the invoice stands on its own.
      if (!isCashPayment && canTakePayment) {
        const invoiceId = invoiceResult?.invoice?.id;
        if (invoiceId) {
          setIsGeneratingPaymentLink(true);
          try {
            const linkResult =
              await generateLinkMutation.mutateAsync(invoiceId);

            if (linkResult?.paymentLink) {
              setPaymentLink(linkResult.paymentLink);
              // Save to localStorage for persistence across navigation
              paymentLinkStorage.savePaymentLink(
                dispatchId,
                linkResult.paymentLink
              );
              // Refresh payment status again to get the updated invoice with payment link
              await paymentStatusResult.refetch();
            }
          } catch (linkError: any) {
            console.error(
              '[PaymentScreen] Error generating payment link:',
              linkError
            );
            // Don't show error toast - payment link generation failure shouldn't block the flow
            // The user can still generate it manually if needed
          } finally {
            setIsGeneratingPaymentLink(false);
          }
        }
      }
    } catch (error: any) {
      console.error('[PaymentScreen] Error creating invoice:', error);

      // Handle 409 Conflict (invoice already exists)
      if (error?.response?.status === 409 || error?.response?.status === 400) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'An invoice already exists for this dispatch';
        showToast('error', errorMessage);
        // Refresh payment status to get the existing invoice
        await paymentStatusResult.refetch();
      } else {
        const errorMessage =
          error?.message ||
          error?.response?.data?.message ||
          'Failed to create invoice';
        showToast('error', errorMessage);
        setErrorState(errorMessage);
      }
    }
  };

  // Phase 2: Handle items change
  const handleItemsChange = (newItems: InvoiceItem[]) => {
    setItems(newItems);
    const total = newItems.reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0
    );
    setAmount(total);
  };

  // Phase 3: Handle generating payment link
  const handleGenerateLink = async () => {
    try {
      if (!companyId) {
        throw new Error('Company ID is not available');
      }

      if (!paymentStatus?.invoice?.id) {
        showToast('error', 'No invoice found');
        return;
      }

      const result = await generateLinkMutation.mutateAsync(
        paymentStatus.invoice.id
      );

      if (!result?.paymentLink) {
        throw new Error('Payment link was not generated');
      }

      setPaymentLink(result.paymentLink);
      // Save to localStorage for persistence across navigation
      paymentLinkStorage.savePaymentLink(dispatchId, result.paymentLink);
      showToast('success', 'Payment link generated');
      await paymentStatusResult.refetch();
    } catch (error: any) {
      console.error('[PaymentScreen] Error generating payment link:', error);
      const errorMessage =
        error?.message ||
        error?.response?.data?.message ||
        'Failed to generate payment link';
      showToast('error', errorMessage);
      setErrorState(errorMessage);
    }
  };

  // Phase 4: Handle sharing payment link
  const handleShareLink = async () => {
    try {
      if (!paymentStatus) {
        showToast('error', 'Payment information not available');
        return;
      }

      const link = paymentLink || paymentStatus.invoice?.paymentLink;
      if (!link) {
        showToast('error', 'No payment link available');
        return;
      }

      // Get amount and dispatch number for share text
      const shareAmount =
        paymentStatus.invoice?.total ?? paymentStatus.suggestedTotal ?? 0;
      const shareDispatchNum = paymentStatus.dispatchNumber || 'this dispatch';
      const shareText = `Please pay $${shareAmount.toFixed(2)} for ${shareDispatchNum}`;

      const shareData = {
        title: 'Payment Link',
        text: shareText,
        url: link,
      };

      // Try native bridge first (for iOS/Android WebViews)
      const isEmbedded =
        typeof (window as any).ReactNativeWebView !== 'undefined' ||
        typeof (window as any).webkit?.messageHandlers?.iosApp !== 'undefined';

      if (isEmbedded) {
        try {
          // Try ReactNativeWebView bridge (Android/iOS)
          if (typeof (window as any).ReactNativeWebView !== 'undefined') {
            (window as any).ReactNativeWebView.postMessage(
              JSON.stringify({
                type: 'SHARE',
                data: {
                  title: shareData.title,
                  text: shareData.text,
                  url: shareData.url,
                },
              })
            );
            showToast('success', 'Opening share...');
            return;
          }
          // Try iOS WebKit bridge
          if (
            typeof (window as any).webkit?.messageHandlers?.iosApp !==
            'undefined'
          ) {
            (window as any).webkit.messageHandlers.iosApp.postMessage({
              type: 'SHARE',
              data: {
                title: shareData.title,
                text: shareData.text,
                url: shareData.url,
              },
            });
            showToast('success', 'Opening share...');
            return;
          }
        } catch (bridgeError) {
          console.error('[PaymentScreen] Native bridge error:', bridgeError);
          // Fall through to Web Share API
        }
      }

      // Use Web Share API if available (mobile browsers, PWA)
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare(shareData)
      ) {
        try {
          await navigator.share(shareData);
          showToast('success', 'Payment link shared');
          return;
        } catch (shareError: any) {
          // User cancelled or share failed, fall through to copy
          if (shareError.name !== 'AbortError') {
            console.error('[PaymentScreen] Share error:', shareError);
          }
        }
      }

      // Fallback: Copy to clipboard
      await handleCopyLink();
    } catch (error: any) {
      console.error('[PaymentScreen] Error sharing link:', error);
      showToast('error', 'Failed to share link');
    }
  };

  // Phase 3: Handle send SMS
  const handleSendSMS = () => {
    try {
      const link = paymentLink || paymentStatus?.invoice?.paymentLink;
      if (!link) {
        showToast('error', 'No payment link available');
        return;
      }

      // Open SMS app with payment link in body
      const smsBody = encodeURIComponent(`Please pay your invoice: ${link}`);
      const smsUrl = `sms:?body=${smsBody}`;
      window.location.href = smsUrl;
    } catch (error: any) {
      console.error('[PaymentScreen] Error opening SMS:', error);
      showToast('error', 'Failed to open SMS app');
    }
  };

  // Handle copy link (kept for fallback in share function)
  const handleCopyLink = async () => {
    try {
      const link = paymentLink || paymentStatus?.invoice?.paymentLink;
      if (!link) {
        showToast('error', 'No payment link available');
        return;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
        setCopied(true);
        showToast('success', 'Link copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for browsers without clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        showToast('success', 'Link copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error: any) {
      console.error('[PaymentScreen] Error copying link:', error);
      showToast('error', 'Failed to copy link');
    }
  };

  // Phase 5: Handle refresh status
  const handleRefreshStatus = async () => {
    try {
      if (!companyId) {
        throw new Error('Company ID is not available');
      }

      if (!paymentStatus?.invoice?.id) {
        showToast('error', 'No invoice found to refresh');
        return;
      }

      await refreshStatusMutation.mutateAsync(paymentStatus.invoice.id);
      await paymentStatusResult.refetch();
      showToast('success', 'Payment status refreshed');
    } catch (error: any) {
      console.error('[PaymentScreen] Error refreshing status:', error);
      const errorMessage =
        error?.message ||
        error?.response?.data?.message ||
        'Failed to refresh status';
      showToast('error', errorMessage);
      setErrorState(errorMessage);
    }
  };

  // Handle mark invoice as paid (for cash payments)
  const handleMarkAsPaid = async () => {
    try {
      if (!companyId) {
        throw new Error('Company ID is not available');
      }

      if (!paymentStatus?.invoice?.id) {
        showToast('error', 'No invoice found');
        return;
      }

      await markAsPaidMutation.mutateAsync(paymentStatus.invoice.id);
      await paymentStatusResult.refetch();
      showToast('success', 'Invoice marked as paid');
    } catch (error: any) {
      console.error('[PaymentScreen] Error marking invoice as paid:', error);
      const errorMessage =
        error?.message ||
        error?.response?.data?.message ||
        'Failed to mark as paid';
      showToast('error', errorMessage);
      setErrorState(errorMessage);
    }
  };

  // Loading state
  if (isProfileLoading || isPaymentLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="bg-primary text-primary-foreground px-4 py-4">
          <div className="flex items-center">
            <button onClick={onBack} className="mr-3 p-1">
              <ArrowLeft size={24} />
            </button>
            <span className="text-xl font-semibold">Invoices</span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin h-8 w-8 text-primary" />
        </div>
      </div>
    );
  }

  // Error state - no companyId
  if (!companyId) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="bg-primary text-primary-foreground px-4 py-4">
          <div className="flex items-center">
            <button onClick={onBack} className="mr-3 p-1">
              <ArrowLeft size={24} />
            </button>
            <span className="text-xl font-semibold">Invoices</span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <p className="text-red-600 mb-4">
              Unable to load company information. Please try again.
            </p>
            <button
              onClick={onBack}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (paymentError || errorState || !paymentStatus) {
    const errorMessage =
      errorState ||
      (paymentError instanceof Error
        ? paymentError.message
        : 'Unknown error') ||
      'Failed to load payment information';

    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="bg-primary text-primary-foreground px-4 py-4">
          <div className="flex items-center">
            <button onClick={onBack} className="mr-3 p-1">
              <ArrowLeft size={24} />
            </button>
            <span className="text-xl font-semibold">Invoices</span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <p className="text-red-600 mb-4">{errorMessage}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => {
                  setErrorState(null);
                  window.location.reload();
                }}
                className="bg-primary text-primary-foreground px-4 py-2 rounded-lg"
              >
                Try Again
              </button>
              <button
                onClick={onBack}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Square or RoadSync, whichever the company uses. Checking Square alone
  // turned every RoadSync company away from taking a payment.
  if (!paymentStatus.paymentConnected) {
    return <PaymentsNotConnected onBack={onBack} />;
  }

  // Safety checks for required fields
  const dispatchNumber = paymentStatus.dispatchNumber || 'N/A';
  const contactName = paymentStatus.contactName || 'Unknown';
  const hasInvoice = paymentStatus.hasInvoice && paymentStatus.invoice;
  const invoiceTotal = paymentStatus.invoice?.total ?? 0;
  const currentPaymentLink =
    paymentLink || paymentStatus?.invoice?.paymentLink || null;
  // The provider's own word comes first: its webhook lands before the
  // invoice is marked paid.
  const providerStatus =
    paymentStatus.paymentProvider === 'ROADSYNC'
      ? paymentStatus.invoice?.roadsyncPaymentStatus
      : paymentStatus.invoice?.squarePaymentStatus;
  const isPaid =
    providerStatus === 'COMPLETED' || paymentStatus?.invoice?.status === 'PAID';
  // Check isCashPayment from invoice, fallback to local paymentType if invoice exists but isCashPayment is undefined
  const isCashPayment =
    paymentStatus?.invoice?.isCashPayment ??
    (hasInvoice && paymentType === 'CASH');

  console.log('[PaymentScreen] Payment flow debug:', {
    hasInvoice,
    invoiceIsCashPayment: paymentStatus?.invoice?.isCashPayment,
    localPaymentType: paymentType,
    resolvedIsCashPayment: isCashPayment,
    isPaid,
    currentPaymentLink,
  });

  // PHASE 1: Basic display - Customer info and amount
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={onBack} className="mr-3 p-1">
              <ArrowLeft size={24} />
            </button>
            <div>
              <span className="text-xl font-semibold">Invoices</span>
              <p className="text-sm text-white/80">{dispatchNumber}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 space-y-4">
        {/* Create Invoice Title Section */}
        {!hasInvoice && (
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Create Invoice</h2>
            <p className="text-sm text-gray-600 mt-1">
              Add charges and generate the payment link
            </p>
          </div>
        )}

        {/* Invoice Display Section (when invoice exists) */}
        {hasInvoice && paymentStatus.invoice && (
          <>
            {/* Total Amount - Centered */}
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Total amount</p>
              <p className="text-3xl font-bold mb-1 text-gray-900">
                ${invoiceTotal.toFixed(2)}
              </p>
              <p className="text-sm text-gray-600">
                {paymentStatus.invoice.invoiceNumber}
              </p>
            </div>

            {/* Dividing Line - Full Width */}
            <div className="border-t border-gray-200 -mx-4" />

            {/* Invoice Details Section */}
            <div className="bg-transparent rounded-lg space-y-3 pt-3">
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-medium text-gray-600">
                  Customer
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {customerName || contactName}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-medium text-gray-600">
                  Payment Type
                </span>
                <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  <CreditCard size={14} />
                  {paymentType}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-medium text-gray-600">
                  Status
                </span>
                <PaymentStatusBadge
                  status={
                    providerStatus || paymentStatus.invoice.status || 'DRAFT'
                  }
                />
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-medium text-gray-600">
                  Created
                </span>
                <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  <Calendar size={14} />
                  {new Date().toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Dividing Line - Full Width between Created and Summary */}
            <div className="border-t border-gray-200 -mx-4"></div>

            {/* Summary Section - Line Items */}
            <div className="bg-transparent rounded-lg px-4">
              <h3 className="text-sm font-bold text-black mb-3">Summary</h3>
              <div className="space-y-2">
                {paymentStatus.invoice?.items &&
                  Array.isArray(paymentStatus.invoice.items) &&
                  paymentStatus.invoice.items.length > 0 &&
                  paymentStatus.invoice.items.map((item, index) => {
                    const quantity =
                      typeof item.quantity === 'number' ? item.quantity : 0;
                    const unitPrice =
                      typeof item.unitPrice === 'number' ? item.unitPrice : 0;
                    const itemTotal = quantity * unitPrice;
                    return (
                      <div key={index}>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-sm text-gray-900">
                            {item.description || 'Item'}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            ${itemTotal.toFixed(2)}
                          </span>
                        </div>
                        {index <
                          (paymentStatus.invoice?.items?.length || 0) - 1 && (
                          <div className="border-b border-gray-200" />
                        )}
                      </div>
                    );
                  })}
                {/* Total */}
                <div className="pt-2 mt-2 border-t-2 border-gray-300">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-900">
                      Total
                    </span>
                    <span className="text-sm font-bold text-gray-900">
                      ${invoiceTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Dividing Line - Full Width between Summary and QR Code */}
            <div className="border-t border-gray-200 -mx-4 mt-4"></div>
          </>
        )}

        {/* Customer Info (when creating invoice) */}
        {!hasInvoice && (
          <div className="bg-transparent rounded-lg">
            <h3 className="text-sm font-bold text-black mb-2">Customer</h3>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Enter customer name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-gray-900 placeholder:text-gray-400 dark:bg-background dark:text-gray-900 dark:border-gray-300 shadow-md"
            />
          </div>
        )}

        {/* Payment Type Field (when creating invoice) */}
        {!hasInvoice && (
          <div className="bg-transparent rounded-lg">
            <label className="block text-sm font-bold text-black mb-2">
              Payment Type
            </label>
            <select
              value={paymentType}
              onChange={(e) =>
                setPaymentType(e.target.value as 'CASH' | 'SQUARE')
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-gray-900 dark:bg-background dark:text-gray-900 dark:border-gray-300 shadow-md"
            >
              <option value="CASH" className="bg-background text-gray-900">
                Cash
              </option>
              <option value="SQUARE" className="bg-background text-gray-900">
                Square
              </option>
            </select>
          </div>
        )}

        {/* Line Items Section (when creating invoice) */}
        {!hasInvoice && (
          <div className="bg-transparent rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-black">Line items</h3>
            </div>
            <InvoiceItemsEditor
              items={items}
              onChange={handleItemsChange}
              availableRates={customerRates}
            />
          </div>
        )}

        {/* Phase 2: Create Invoice Button (if no invoice) */}
        {!hasInvoice && canCreateInvoice && (
          <button
            onClick={handleCreateInvoice}
            disabled={
              createInvoiceMutation.isPending ||
              (amount <= 0 && items.length === 0)
            }
            className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {createInvoiceMutation.isPending ? (
              <>
                <Loader2 className="animate-spin h-4 w-4" />
                Creating Invoice...
              </>
            ) : (
              <>
                <CreditCard size={18} />
                Create Invoice
              </>
            )}
          </button>
        )}

        {/* Without the key the invoice is made, but collecting is for someone else. */}
        {hasInvoice && !isPaid && permissionsState.isError && (
          <PermissionsErrorAlert onRetry={permissionsState.refetch} />
        )}
        {hasInvoice && !isPaid && permissionsKnown && !canTakePayment && (
          <Alert tone="neutral" title="Invoice created">
            Taking payment needs the take payment permission. An owner or admin
            can collect it.
          </Alert>
        )}

        {/* Phase 3-6: Payment Link Section (if invoice exists and not paid) */}
        {hasInvoice && !isPaid && canTakePayment && (
          <>
            {/* Cash Payment Flow - Show Mark as Paid button */}
            {isCashPayment ? (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                  <p className="text-amber-800 font-medium">Cash Payment</p>
                  <p className="text-amber-600 text-sm mt-1">
                    Collect ${invoiceTotal.toFixed(2)} in cash and mark as paid
                  </p>
                </div>
                <button
                  onClick={handleMarkAsPaid}
                  disabled={markAsPaidMutation.isPending}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2 shadow-md"
                >
                  {markAsPaidMutation.isPending ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4" />
                      Marking as Paid...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Mark as Paid
                    </>
                  )}
                </button>
              </div>
            ) : (
              <>
                {/* Square Payment Flow - QR Code and payment link */}
                {isGeneratingPaymentLink || generateLinkMutation.isPending ? (
                  <div className="bg-background rounded-lg p-8 border border-gray-200 shadow-md">
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 className="animate-spin h-12 w-12 text-primary mb-4" />
                      <p className="text-gray-600 font-medium">
                        Generating payment link...
                      </p>
                    </div>
                  </div>
                ) : currentPaymentLink ? (
                  <>
                    <QRCodeCard
                      paymentLink={currentPaymentLink}
                      amount={invoiceTotal}
                    />

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      <button
                        onClick={handleShareLink}
                        className="w-full bg-background text-black py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 shadow-md"
                      >
                        <Share2 size={18} className="text-black" />
                        Share Payment Link
                      </button>

                      {/* Send via SMS button - only shows when user has permission */}
                      {canSendSMS && (
                        <button
                          onClick={handleSendSMS}
                          className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 shadow-md"
                        >
                          <MessageSquare size={18} />
                          Send via SMS
                        </button>
                      )}
                    </div>

                    {/* Refresh Status */}
                    <button
                      onClick={handleRefreshStatus}
                      disabled={refreshStatusMutation.isPending}
                      className="w-full bg-background border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 shadow-md"
                    >
                      {refreshStatusMutation.isPending ? (
                        <>
                          <Loader2 className="animate-spin h-4 w-4" />
                          Refreshing...
                        </>
                      ) : (
                        <>
                          <RefreshCw size={18} />
                          Refresh Payment Status
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  /* No payment link - show error state with retry */
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center space-y-3">
                    <p className="text-amber-800 font-medium">
                      Payment link not available
                    </p>
                    <p className="text-amber-600 text-sm">
                      There was an issue generating the payment link.
                    </p>
                    <button
                      onClick={handleGenerateLink}
                      disabled={generateLinkMutation.isPending}
                      className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {generateLinkMutation.isPending ? (
                        <>
                          <Loader2 className="animate-spin h-4 w-4" />
                          Retrying...
                        </>
                      ) : (
                        <>
                          <RefreshCw size={16} />
                          Retry
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Phase 6: Paid Status */}
        {isPaid && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <Check className="mx-auto h-12 w-12 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-green-800">
              Payment Received!
            </h3>
            <p className="text-green-600 mt-1">
              ${invoiceTotal.toFixed(2)} has been paid.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentScreen;
